import type { ReactNode } from 'react';
import type { NavToolMenuItem } from '@/components/NavMenus/navMenuConfig';

export default function ToolBannerPreview({ toolId }: { toolId: NavToolMenuItem['id'] }) {
  switch (toolId) {
    case 'mcp':
      return <McpPreview />;
    case 'ai':
      return <AidaPreview />;
    case 'api':
      return <ApiPreview />;
    case 'geo-browse':
      return <ExpressNetPreview />;
    case 'gsea-ranked':
    case 'enrichment':
      return <EnrichmentPreview />;
    case 'download':
      return <DownloadPreview />;
    case 'r-package':
      return <RPackagePreview />;
    case 'cytoscape':
    case 'ontology-explorer':
      return <NetworkPreview />;
    case 'advanced-search':
      return <SearchPreview />;
    default:
      return <McpPreview />;
  }
}

function PreviewFrame({ children, label }: { children: ReactNode; label: string }) {
  return (
    <svg
      className="tools-page__banner-preview"
      viewBox="0 0 320 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
    >
      <rect x="8" y="12" width="304" height="176" rx="16" fill="#fff" stroke="#e4e7ef" strokeWidth="1.5" />
      <rect x="8" y="12" width="304" height="28" rx="16" fill="#f4f6fb" />
      <rect x="8" y="28" width="304" height="12" fill="#f4f6fb" />
      <circle cx="28" cy="26" r="3.5" fill="#c5cad8" />
      <circle cx="42" cy="26" r="3.5" fill="#c5cad8" />
      <circle cx="56" cy="26" r="3.5" fill="#c5cad8" />
      <text x="72" y="30" fill="#8b93a7" fontSize="9" fontFamily="ui-sans-serif, system-ui, sans-serif">
        {label}
      </text>
      {children}
    </svg>
  );
}

function McpPreview() {
  return (
    <PreviewFrame label="MCP Connector">
      {/* Agent */}
      <rect x="28" y="56" width="88" height="108" rx="12" fill="#f7f8fc" stroke="#d8dce8" />
      <circle cx="72" cy="88" r="16" fill="#eef0f7" stroke="#0a104f" strokeWidth="1.4" />
      <circle cx="66" cy="86" r="2" fill="#0a104f" />
      <circle cx="78" cy="86" r="2" fill="#0a104f" />
      <path d="M66 94c2.5 3 9.5 3 12 0" stroke="#0a104f" strokeWidth="1.4" strokeLinecap="round" />
      <text
        x="48"
        y="122"
        fill="#0a104f"
        fontSize="10"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        Agent
      </text>
      <rect x="42" y="132" width="60" height="8" rx="4" fill="#dfe3ee" />
      <rect x="48" y="146" width="48" height="8" rx="4" fill="#eceff6" />

      {/* Flow */}
      <path d="M124 110H156" stroke="#be0080" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M148 104l8 6-8 6"
        stroke="#be0080"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M156 128H124" stroke="#9aa3b8" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 3" />
      <path
        d="M132 122l-8 6 8 6"
        stroke="#9aa3b8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* DISGENET / MCP */}
      <rect x="168" y="56" width="124" height="108" rx="12" fill="#0a104f" />
      <text
        x="184"
        y="82"
        fill="#fff"
        fontSize="10"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        DISGENET
      </text>
      <rect x="184" y="90" width="92" height="10" rx="5" fill="#be0080" opacity="0.9" />
      <rect x="184" y="108" width="72" height="8" rx="4" fill="#fff" opacity="0.25" />
      <rect x="184" y="120" width="84" height="8" rx="4" fill="#fff" opacity="0.18" />
      <rect x="184" y="132" width="56" height="8" rx="4" fill="#fff" opacity="0.12" />
      <text
        x="185"
        y="155"
        fill="#fff"
        fontSize="9"
        opacity="0.7"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        MCP endpoint
      </text>
    </PreviewFrame>
  );
}

function AidaPreview() {
  return (
    <PreviewFrame label="AIDA">
      <rect x="36" y="56" width="160" height="44" rx="12" fill="#f4f6fb" stroke="#d8dce8" />
      <text x="50" y="76" fill="#5c657a" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">
        Which genes are linked to
      </text>
      <text x="50" y="90" fill="#5c657a" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">
        Alzheimer disease?
      </text>
      <rect x="120" y="112" width="164" height="52" rx="12" fill="#0a104f" />
      <text x="136" y="134" fill="#fff" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">
        Top associations with
      </text>
      <text x="136" y="150" fill="#fff" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">
        evidence scores…
      </text>
      <circle cx="56" cy="168" r="8" fill="#be0080" opacity="0.85" />
      <circle cx="56" cy="168" r="3" fill="#fff" />
    </PreviewFrame>
  );
}

function ApiPreview() {
  return (
    <PreviewFrame label="REST API">
      <rect x="28" y="52" width="264" height="124" rx="10" fill="#0a104f" />
      <text
        x="44"
        y="78"
        fill="#7ddea8"
        fontSize="11"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        GET /gda/gene/APP
      </text>
      <text
        x="44"
        y="100"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        {'{'}
      </text>
      <text
        x="56"
        y="118"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        "gene": "APP",
      </text>
      <text
        x="56"
        y="136"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        "associations": 1284
      </text>
      <text
        x="44"
        y="154"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        {'}'}
      </text>
    </PreviewFrame>
  );
}

function ExpressNetPreview() {
  return (
    <PreviewFrame label="ExpressNET">
      <rect x="28" y="52" width="120" height="124" rx="10" fill="#f7f8fc" stroke="#d8dce8" />
      <rect x="40" y="68" width="96" height="10" rx="5" fill="#dfe3ee" />
      <rect x="40" y="88" width="72" height="8" rx="4" fill="#eceff6" />
      <rect x="40" y="104" width="84" height="8" rx="4" fill="#eceff6" />
      <rect x="40" y="120" width="64" height="8" rx="4" fill="#eceff6" />
      <rect x="40" y="148" width="96" height="14" rx="7" fill="#0a104f" />
      <rect x="160" y="52" width="132" height="124" rx="10" fill="#fff" stroke="#d8dce8" />
      <path
        d="M176 150c12-40 20-64 28-64s14 36 28 52 16 12 28-8"
        stroke="#be0080"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M176 150c10-24 18-40 26-40s12 22 24 34 18 10 28-4"
        stroke="#0a104f"
        strokeWidth="1.5"
        fill="none"
        opacity="0.45"
        strokeLinecap="round"
      />
      <circle cx="204" cy="86" r="3" fill="#be0080" />
      <circle cx="260" cy="138" r="3" fill="#0a104f" />
    </PreviewFrame>
  );
}

function EnrichmentPreview() {
  return (
    <PreviewFrame label="Enrichment">
      <rect x="36" y="148" width="28" height="28" rx="4" fill="#dfe3ee" />
      <rect x="76" y="120" width="28" height="56" rx="4" fill="#c5cad8" />
      <rect x="116" y="88" width="28" height="88" rx="4" fill="#0a104f" />
      <rect x="156" y="104" width="28" height="72" rx="4" fill="#6b7390" />
      <rect x="196" y="72" width="28" height="104" rx="4" fill="#be0080" opacity="0.85" />
      <rect x="236" y="128" width="28" height="48" rx="4" fill="#dfe3ee" />
      <line x1="32" y1="176" x2="280" y2="176" stroke="#d8dce8" strokeWidth="1.5" />
    </PreviewFrame>
  );
}

function DownloadPreview() {
  return (
    <PreviewFrame label="Download">
      <rect x="88" y="56" width="144" height="100" rx="12" fill="#f7f8fc" stroke="#d8dce8" />
      <rect x="108" y="76" width="104" height="10" rx="5" fill="#dfe3ee" />
      <rect x="118" y="96" width="84" height="8" rx="4" fill="#eceff6" />
      <rect x="118" y="112" width="84" height="8" rx="4" fill="#eceff6" />
      <path d="M160 128v28" stroke="#0a104f" strokeWidth="2.5" strokeLinecap="round" />
      <path
        d="M148 144l12 12 12-12"
        stroke="#0a104f"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="120" y="164" width="80" height="12" rx="6" fill="#be0080" />
    </PreviewFrame>
  );
}

function RPackagePreview() {
  return (
    <PreviewFrame label="disgenet2r">
      <rect x="28" y="52" width="264" height="124" rx="10" fill="#0a104f" />
      <text
        x="44"
        y="80"
        fill="#9ad0ff"
        fontSize="11"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        library(disgenet2r)
      </text>
      <text
        x="44"
        y="104"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        gdas &lt;- gene2disease(
      </text>
      <text
        x="56"
        y="124"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        gene = "BRCA1",
      </text>
      <text
        x="56"
        y="144"
        fill="#c9d0e0"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        vocabulary = "UMLS")
      </text>
    </PreviewFrame>
  );
}

function NetworkPreview() {
  return (
    <PreviewFrame label="Network">
      <circle cx="160" cy="110" r="18" fill="#0a104f" />
      <circle cx="96" cy="78" r="12" fill="#be0080" opacity="0.85" />
      <circle cx="230" cy="74" r="12" fill="#6b7390" />
      <circle cx="84" cy="148" r="11" fill="#c5cad8" />
      <circle cx="236" cy="150" r="13" fill="#dfe3ee" stroke="#0a104f" />
      <circle cx="160" cy="168" r="10" fill="#eceff6" stroke="#0a104f" />
      <line x1="160" y1="110" x2="96" y2="78" stroke="#0a104f" strokeWidth="1.5" opacity="0.45" />
      <line x1="160" y1="110" x2="230" y2="74" stroke="#0a104f" strokeWidth="1.5" opacity="0.45" />
      <line x1="160" y1="110" x2="84" y2="148" stroke="#0a104f" strokeWidth="1.5" opacity="0.45" />
      <line x1="160" y1="110" x2="236" y2="150" stroke="#0a104f" strokeWidth="1.5" opacity="0.45" />
      <line x1="160" y1="110" x2="160" y2="168" stroke="#0a104f" strokeWidth="1.5" opacity="0.45" />
    </PreviewFrame>
  );
}

function SearchPreview() {
  return (
    <PreviewFrame label="Advanced Search">
      <rect x="36" y="60" width="248" height="36" rx="10" fill="#fff" stroke="#d8dce8" strokeWidth="1.5" />
      <circle cx="56" cy="78" r="8" stroke="#8b93a7" strokeWidth="1.6" fill="none" />
      <path d="M62 84l6 6" stroke="#8b93a7" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="72" y="73" width="120" height="10" rx="5" fill="#eceff6" />
      <rect x="36" y="112" width="72" height="22" rx="11" fill="#0a104f" />
      <rect x="116" y="112" width="72" height="22" rx="11" fill="#f4f6fb" stroke="#d8dce8" />
      <rect x="196" y="112" width="88" height="22" rx="11" fill="#f4f6fb" stroke="#d8dce8" />
      <rect x="36" y="148" width="248" height="10" rx="5" fill="#eceff6" />
      <rect x="36" y="166" width="180" height="10" rx="5" fill="#f4f6fb" />
    </PreviewFrame>
  );
}
